<template>
  <div class="site-page">
    <site-header />

    <section v-if="useBannerLayout" class="site-page__hero">
      <div class="core-container">
        <h1 class="site-page__hero-title">
          {{ loading ? '加载中...' : page.title }}
        </h1>
      </div>
    </section>

    <main :class="['site-page__main', { 'site-page__main--with-hero': useBannerLayout }]">
      <div class="core-container">
        <section :class="['site-page__card', { 'site-page__card--banner': useBannerLayout }]">
          <div class="site-page__inner">
            <p v-if="loading" class="site-page__status">加载中...</p>
            <p v-else-if="error" class="site-page__status">{{ error }}</p>
            <template v-else>
              <h1 v-if="!useBannerLayout" class="site-page__title">{{ page.title }}</h1>
              <div
                v-if="page.content"
                class="site-page__content"
                v-html="page.content"
              />
              <p v-else class="site-page__empty">内容暂未配置，请稍后再看。</p>
            </template>
          </div>
        </section>
      </div>
    </main>

    <site-footer />
  </div>
</template>

<script>
import SiteHeader from '@/components/home/SiteHeader.vue'
import SiteFooter from '@/components/home/SiteFooter.vue'
import { fetchSitePage } from '@/api/site'
import {
  SITE_PAGE_DEFINITIONS,
  findSitePageDefinition
} from '@/constants/sitePages'
import { buildRichEditorFontCss } from '@/constants/richEditor'

export default {
  name: 'SitePageView',
  components: {
    SiteHeader,
    SiteFooter
  },
  data() {
    return {
      loading: true,
      error: null,
      pageLayout: 'card',
      page: {
        title: '',
        content: ''
      }
    }
  },
  computed: {
    useBannerLayout() {
      return this.pageLayout === 'banner'
    }
  },
  mounted() {
    this.injectContentStyles()
  },
  watch: {
    '$route.path': {
      immediate: true,
      handler() {
        this.loadPage()
      }
    }
  },
  methods: {
    injectContentStyles() {
      const styleId = 'site-page-rich-content-style'
      if (document.getElementById(styleId)) return

      const style = document.createElement('style')
      style.id = styleId
      style.textContent = buildRichEditorFontCss('.site-page__content')
      document.head.appendChild(style)
    },
    resolvePageKey() {
      if (this.$route.meta && this.$route.meta.sitePageKey) {
        return this.$route.meta.sitePageKey
      }
      const definition = SITE_PAGE_DEFINITIONS.find(
        item => item.path === this.$route.path
      )
      return definition ? definition.key : ''
    },
    loadPage() {
      const key = this.resolvePageKey()
      const definition = findSitePageDefinition(key)

      if (!definition) {
        this.loading = false
        this.error = '页面不存在'
        return
      }

      this.loading = true
      this.error = null
      this.pageLayout = definition.layout || 'card'
      this.page = {
        title: definition.label,
        content: ''
      }

      fetchSitePage(key)
        .then(result => {
          if (result.status !== 1 || !result.data) {
            this.error = result.desc || result.message || '页面不存在'
            return
          }
          this.pageLayout = result.data.layout || definition.layout || 'card'
          this.page = {
            title: result.data.title || definition.label,
            content: result.data.content || ''
          }
        })
        .catch(() => {
          this.error = '加载失败，请稍后重试'
        })
        .finally(() => {
          this.loading = false
        })
    }
  }
}
</script>

<style scoped>
.site-page {
  min-height: 100vh;
  background: var(--color-bg-main);
}

.site-page__hero {
  background: linear-gradient(
    90deg,
    var(--color-banner-fill-left) 0%,
    var(--color-banner-fill-left) 42%,
    var(--color-banner-fill-right) 58%,
    var(--color-banner-fill-right) 100%
  );
  padding: var(--space-2xl) 0;
}

.site-page__hero-title {
  margin: 0;
  text-align: center;
  font-size: var(--font-size-3xl);
  font-weight: 600;
  color: var(--color-text-inverse);
  line-height: 1.3;
}

.site-page__main {
  padding: var(--space-lg) 0 var(--space-xl);
}

.site-page__main--with-hero {
  padding-top: var(--space-lg);
}

.site-page__card {
  background: var(--color-bg-page);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-panel);
  padding: var(--space-xl);
  min-height: 320px;
}

.site-page__inner {
  width: 100%;
  max-width: var(--site-page-content-width);
  margin: 0 auto;
}

.site-page__card--banner .site-page__inner {
  max-width: 100%;
}

.site-page__title {
  margin-bottom: var(--space-lg);
  font-size: var(--font-size-xl);
  font-weight: 600;
  color: var(--color-text-primary);
  text-align: center;
}

.site-page__content {
  font-size: var(--font-size-md);
  line-height: 1.9;
  color: var(--color-text-primary);
  word-break: break-word;
  text-align: center;
}

.site-page__content :deep(p),
.site-page__content :deep(h1),
.site-page__content :deep(h2),
.site-page__content :deep(h3),
.site-page__content :deep(ul),
.site-page__content :deep(ol) {
  text-align: left;
}

.site-page__content :deep(h2) {
  margin: var(--space-lg) 0 var(--space-md);
  font-size: var(--font-size-xl);
  font-weight: 600;
  color: var(--color-primary);
}

.site-page__content :deep(h3) {
  margin: var(--space-md) 0 var(--space-sm);
  font-size: var(--font-size-lg);
  font-weight: 600;
  color: var(--color-text-primary);
}

.site-page__content :deep(p) {
  margin-bottom: var(--space-md);
}

.site-page__content :deep(p:last-child) {
  margin-bottom: 0;
}

.site-page__content :deep(ul),
.site-page__content :deep(ol) {
  margin: 0 0 var(--space-md);
  padding-left: var(--space-lg);
}

.site-page__content :deep(li) {
  margin-bottom: var(--space-sm);
}

.site-page__content :deep(img) {
  width: 100%;
  max-width: 100%;
  height: auto;
  display: block;
  margin: var(--space-md) auto;
  border-radius: var(--radius-sm);
}

.site-page__content :deep(.ql-align-center) {
  text-align: center;
}

.site-page__content :deep(.ql-align-right) {
  text-align: right;
}

.site-page__content :deep(.ql-align-justify) {
  text-align: justify;
}

.site-page__status,
.site-page__empty {
  font-size: var(--font-size-md);
  color: var(--color-text-muted);
  line-height: 1.8;
  text-align: center;
}
</style>
